import { combineSlices, configureStore, createSelector } from "@reduxjs/toolkit"
import { subjectSelectors, subjectsSlice } from "./subjects-slice"
import { prioritiesSelectors, prioritiesSlice } from "./priorities-slice"
import { eventsSelectors, eventsSlice } from "./events-slice"
import { preferencesSelectors, preferencesSlice } from "./preferences-slice"
import {
  createDispatchHook,
  createSelectorHook,
  ReactReduxContextValue,
} from "react-redux"
import { createContext } from "react"
import { GetSteppedColours, neutralColour } from "@/constants/priorityColours"
import { PreferenceError, PreferenceId } from "./types"

const reducer = combineSlices(
  eventsSlice,
  preferencesSlice,
  subjectsSlice,
  prioritiesSlice,
)

export function createPageStore(route: { href: string; data: object }) {
  return configureStore({
    reducer,
    middleware(getDefaultMiddleware) {
      return getDefaultMiddleware({ thunk: { extraArgument: { route } } })
    },
  })
}

export type PageState = ReturnType<typeof reducer>
export type PageStore = ReturnType<typeof createPageStore>
export type PageDispatch = PageStore["dispatch"]
export const PageReduxContext =
  createContext<ReactReduxContextValue<any> | null>(null)
export const usePageSelector =
  createSelectorHook(PageReduxContext).withTypes<PageState>()
export const usePageDispatch =
  createDispatchHook(PageReduxContext).withTypes<PageDispatch>()

export const selectCalendarEvents = createSelector(
  [
    eventsSelectors.selectBaseCycleEvents,
    eventsSelectors.selectHighlightedPreferenceId,
    eventsSelectors.selectHighlightedPriorityId,
    (state: PageState) => state.priorities,
    (state: PageState) => state.subjects,
    (state: PageState) => state.preferences,
    (state: PageState) => state.events,
    (_, { getColours }: { getColours: GetSteppedColours }) => getColours,
    (
      _,
      {
        buildEventTitle,
      }: {
        buildEventTitle: (
          priorityRank: number,
          preferenceRank: number,
          subjectCode: string,
        ) => string
      },
    ) => buildEventTitle,
  ],
  (
    cycleEvents,
    highlightedPreferenceId,
    highlightedPriorityId,
    priorities,
    subjects,
    preferences,
    events,
    getColours,
    buildEventTitle,
  ) =>
    cycleEvents
      .map(
        ({ preferenceId, priorityId, subjectId, sessionIndex, ...event }) => {
          const preference = preferencesSelectors.selectPreference(
            { preferences },
            preferenceId,
          )
          const priority = prioritiesSelectors.selectPriority(
            { priorities },
            priorityId,
          )

          if (
            priority.preferenceId !== preferenceId &&
            (priority.preferenceId !== null ||
              preferenceId !== preferences.selectedPreferenceId)
          ) {
            return null
          }

          const priorityConflicts =
            eventsSelectors.selectPreferencePriorityConflicts(
              { events },
              {
                preferenceId,
                priorityId,
              },
            )

          let colours = neutralColour
          if (priority.preferenceId !== null) {
            colours = getColours(String(subjectId), preference.rank - 1)
          }

          const { code } = subjectSelectors.selectSubject(
            { subjects },
            subjectId,
          )

          return {
            ...event,
            eventTitle: buildEventTitle(
              priority.priority,
              preference.rank,
              code,
            ),
            extendedProps: {
              priorityConflicts: priorityConflicts.length > 0,
              preferenceInvalid:
                preference.issues.includes("student_ineligible"),
              priorityHighlighted: highlightedPriorityId === priorityId,
              preferenceHighlighted: highlightedPreferenceId === preferenceId,
              preferenceId,
              priorityId,
              timetableSessionIndex: sessionIndex,
              colours,
            },
          }
        },
      )
      .filter((e) => e !== null),
)
export const selectSubjectErrors = createSelector(
  [
    preferencesSelectors.selectInvalidPreferences,
    preferencesSelectors.selectIneligiblePreferences,
    eventsSelectors.selectSubjectConflicts,
  ],
  (invalidPreferences, ineligiblePreferences, conflicts) => {
    const errors: PreferenceError[] = []
    if (conflicts.length > 0) errors.push({ kind: "conflict", conflicts })
    if (invalidPreferences.length > 0)
      errors.push({
        kind: "invalidClassDocument",
        preferences: invalidPreferences,
      })
    if (ineligiblePreferences.length > 0)
      errors.push({
        kind: "studentIneligible",
        preferences: ineligiblePreferences,
      })
    return errors
  },
)

export const selectPreferenceErrors = createSelector(
  [
    (state: PageState) => state.preferences,
    (_, { preferenceId }: { preferenceId: PreferenceId }) => preferenceId,
    eventsSelectors.selectPreferencePriorityConflicts,
  ],
  (preferences, preferenceId, conflicts) => {
    const preference = preferencesSelectors.selectPreference(
      { preferences },
      preferenceId,
    )
    const errors: PreferenceError[] = []

    if (conflicts.length > 0) errors.push({ kind: "conflict", conflicts })
    if (preference.issues.includes("invalid_class_document"))
      errors.push({ kind: "invalidClassDocument", preferences: [preference] })
    if (preference.issues.includes("student_ineligible"))
      errors.push({ kind: "studentIneligible", preferences: [preference] })

    return errors
  },
)
